import { useState } from "react";
import type { FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db8ts68kk00l6eltrmlg";

type FormStatus = "idle" | "submitting" | "success" | "error";

const topics = ["内容建议", "报错反馈", "合作投稿", "商务合作"];

export default function FeedbackForm() {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    formData.delete("contact_alt");
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const name = String(formData.get("name") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const message = String(formData.get("message") ?? "").trim();

    if (!name || !email || !message) {
      setStatus("error");
      setFormError("请完整填写称呼、邮箱与反馈内容。");
      return;
    }
    if (message.length > 500) {
      setStatus("error");
      setFormError("反馈内容不能超过 500 个字符。");
      return;
    }

    const body = new URLSearchParams();
    formData.forEach((value, key) => body.append(key, String(value)));

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const code = parsed?.code;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后再试。");
    }
  };

  return (
    <div id="feedback" className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
      <h3 className="flex items-center gap-2 text-base font-bold text-foreground-950">
        <i className="ri-chat-smile-2-line text-primary-500" />
        用户反馈
      </h3>
      <p className="mt-2 text-sm text-foreground-500">
        对噜噜社有任何建议或想投稿？留下你的想法，我们会认真阅读每一条反馈。
      </p>

      <form id="lulu-feedback-form" data-readdy-form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-4">
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex-1">
            <label htmlFor="fb-name" className="mb-1.5 block text-xs font-medium text-foreground-500">
              称呼
            </label>
            <input
              id="fb-name"
              name="name"
              type="text"
              placeholder="怎么称呼你"
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none focus:border-primary-400"
            />
          </div>
          <div className="flex-1">
            <label htmlFor="fb-email" className="mb-1.5 block text-xs font-medium text-foreground-500">
              邮箱
            </label>
            <input
              id="fb-email"
              name="email"
              type="email"
              placeholder="用于接收回复"
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none focus:border-primary-400"
            />
          </div>
        </div>

        <div>
          <label htmlFor="fb-topic" className="mb-1.5 block text-xs font-medium text-foreground-500">
            反馈类型
          </label>
          <select
            id="fb-topic"
            name="topic"
            defaultValue={topics[0]}
            className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none focus:border-primary-400"
          >
            {topics.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="fb-message" className="mb-1.5 block text-xs font-medium text-foreground-500">
            反馈内容（不超过 500 字）
          </label>
          <textarea
            id="fb-message"
            name="message"
            rows={5}
            maxLength={500}
            placeholder="请描述你的建议、遇到的问题或想投稿的内容…"
            className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm leading-relaxed text-foreground-900 outline-none focus:border-primary-400"
          />
        </div>

        <input
          className="form-field-extra"
          type="text"
          name="contact_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="submit"
            disabled={status === "submitting"}
            className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {status === "submitting" ? "提交中…" : "提交反馈"}
            <i className="ri-send-plane-line" />
          </button>
          {status === "success" && (
            <p className="text-sm font-medium text-primary-600">提交成功，感谢你的反馈！</p>
          )}
          {status === "error" && <p className="text-sm font-medium text-accent-700">{formError}</p>}
        </div>
      </form>
    </div>
  );
}